import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import { trpc } from '@/lib/trpc';
import type {
    WorkspaceWithPricing,
    WorkspaceWithRole,
    WorkspaceRole,
} from '@/types/workspace';

const WORKSPACE_STORAGE_KEY = 'reflow_current_workspace';

interface WorkspaceContextType {
    // Current workspace state
    currentWorkspace: WorkspaceWithRole | null;
    workspaces: WorkspaceWithPricing[];
    userRole: WorkspaceRole | null;

    // Loading states
    loading: boolean;
    error: string | null;

    // Actions
    switchWorkspace: (workspaceId: string) => Promise<void>;
    refreshWorkspaces: () => Promise<void>;
    refreshCurrentWorkspace: () => Promise<void>;
    clearWorkspace: () => void;
    deleteCurrentWorkspace: () => Promise<void>;

    // Helper getters
    isAdmin: boolean;
    isMember: boolean;
    hasWorkspace: boolean;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
    const { user, loading: authLoading } = useAuth();
    const utils = trpc.useUtils();
    const deleteWorkspaceMutation = trpc.workspace.delete.useMutation();

    const [workspaces, setWorkspaces] = useState<WorkspaceWithPricing[]>([]);
    const [currentWorkspace, setCurrentWorkspace] = useState<WorkspaceWithRole | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Load workspaces when user is authenticated
    const refreshWorkspaces = useCallback(async () => {
        if (!user) {
            setWorkspaces([]);
            setLoading(false);
            return;
        }

        try {
            setError(null);
            const data = await utils.workspace.list.fetch().then((r: any) => r.workspaces ?? r);
            setWorkspaces(data);
        } catch (err) {
            console.error('Failed to fetch workspaces:', err);
            setError(err instanceof Error ? err.message : 'Failed to load workspaces');
            setWorkspaces([]);
        }
    }, [user, utils]);

    // Load a specific workspace with full details
    const refreshCurrentWorkspace = useCallback(async () => {
        if (!currentWorkspace?.id) return;

        try {
            const data = await utils.workspace.get.fetch({ workspaceId: currentWorkspace.id }) as unknown as WorkspaceWithRole;
            setCurrentWorkspace(data);
        } catch (err) {
            console.error('Failed to refresh current workspace:', err);
        }
    }, [currentWorkspace?.id, utils]);

    // Switch to a different workspace
    const switchWorkspace = useCallback(async (workspaceId: string) => {
        if (!user) return;

        try {
            setLoading(true);
            setError(null);

            const workspace = await utils.workspace.get.fetch({ workspaceId }) as unknown as WorkspaceWithRole;
            setCurrentWorkspace(workspace);

            // Persist selection
            localStorage.setItem(WORKSPACE_STORAGE_KEY, workspaceId);
        } catch (err) {
            console.error('Failed to switch workspace:', err);
            setError(err instanceof Error ? err.message : 'Failed to switch workspace');
            throw err;
        } finally {
            setLoading(false);
        }
    }, [user, utils]);

    // Clear current workspace (for logout or workspace deletion)
    const clearWorkspace = useCallback(() => {
        setCurrentWorkspace(null);
        localStorage.removeItem(WORKSPACE_STORAGE_KEY);
    }, []);

    // Delete the current workspace
    const deleteCurrentWorkspace = useCallback(async () => {
        if (!currentWorkspace || !user) return;

        try {
            setLoading(true);
            setError(null);

            await deleteWorkspaceMutation.mutateAsync({ workspaceId: currentWorkspace.id });

            // Clear current workspace
            setCurrentWorkspace(null);
            localStorage.removeItem(WORKSPACE_STORAGE_KEY);

            // Refresh workspaces list
            const data = await utils.workspace.list.fetch().then((r: any) => r.workspaces ?? r);
            setWorkspaces(data);

            // Switch to first available workspace if any
            if (data.length > 0) {
                const workspace = await utils.workspace.get.fetch({ workspaceId: data[0].id }) as unknown as WorkspaceWithRole;
                setCurrentWorkspace(workspace);
                localStorage.setItem(WORKSPACE_STORAGE_KEY, data[0].id);
            }
        } catch (err) {
            console.error('Failed to delete workspace:', err);
            setError(err instanceof Error ? err.message : 'Failed to delete workspace');
            throw err;
        } finally {
            setLoading(false);
        }
    }, [currentWorkspace, user, utils, deleteWorkspaceMutation]);

    // Track the user ID to prevent re-initialization on token refresh
    const initializedForUserRef = useRef<string | null>(null);

    // Initialize: Load workspaces and restore last selected workspace
    useEffect(() => {
        if (authLoading) return;

        if (!user) {
            setWorkspaces([]);
            setCurrentWorkspace(null);
            setLoading(false);
            initializedForUserRef.current = null;
            return;
        }

        // Skip re-initialization if we already initialized for this user
        // This prevents reloading when Supabase refreshes the token on tab focus
        if (initializedForUserRef.current === user.id) {
            return;
        }

        const initialize = async () => {
            setLoading(true);
            try {
                // First, load all workspaces
                const data = await utils.workspace.list.fetch().then((r: any) => r.workspaces ?? r);
                setWorkspaces(data);

                if (data.length === 0) {
                    setCurrentWorkspace(null);
                    localStorage.removeItem(WORKSPACE_STORAGE_KEY);
                    initializedForUserRef.current = user.id;
                    return;
                }

                // Try to restore the last selected workspace
                const savedWorkspaceId = localStorage.getItem(WORKSPACE_STORAGE_KEY);
                const targetWorkspaceId = savedWorkspaceId && data.some((w: any) => w.id === savedWorkspaceId)
                    ? savedWorkspaceId
                    : data[0].id;

                // Load full workspace details with user role
                const workspace = await utils.workspace.get.fetch({ workspaceId: targetWorkspaceId }) as unknown as WorkspaceWithRole;
                setCurrentWorkspace(workspace);
                localStorage.setItem(WORKSPACE_STORAGE_KEY, targetWorkspaceId);
                
                // Mark as initialized for this user
                initializedForUserRef.current = user.id;
            } catch (err) {
                console.error('Failed to initialize workspaces:', err);
                setError(err instanceof Error ? err.message : 'Failed to load workspaces');
            } finally {
                setLoading(false);
            }
        };

        initialize();
    }, [user, authLoading]);

    // Computed values
    const userRole = currentWorkspace?.userRole ?? null;
    const isAdmin = userRole === 'admin';
    const isMember = userRole === 'member';
    const hasWorkspace = currentWorkspace !== null;

    const value = useMemo(() => ({
        currentWorkspace,
        workspaces,
        userRole,
        loading,
        error,
        switchWorkspace,
        refreshWorkspaces,
        refreshCurrentWorkspace,
        clearWorkspace,
        deleteCurrentWorkspace,
        isAdmin,
        isMember,
        hasWorkspace,
    }), [
        currentWorkspace,
        workspaces,
        userRole,
        loading,
        error,
        switchWorkspace,
        refreshWorkspaces,
        refreshCurrentWorkspace,
        clearWorkspace,
        deleteCurrentWorkspace,
        isAdmin,
        isMember,
        hasWorkspace,
    ]);

    return (
        <WorkspaceContext.Provider value={value}>
            {children}
        </WorkspaceContext.Provider>
    );
}

export function useWorkspace() {
    const context = useContext(WorkspaceContext);
    if (context === undefined) {
        throw new Error('useWorkspace must be used within a WorkspaceProvider');
    }
    return context;
}
